import SEO from '../components/SEO';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

import { useInView } from 'react-intersection-observer';
import HeroImage from '../assets/hero.webp';
import Hero1Image from '../assets/hero1.webp';
import seed from '../assets/seed.webp';
import machine from '../assets/machin.webp';
import irrigation from '../assets/irrigation.webp';
import about2 from '../assets/about2.webp';
import croppest from '../assets/croppest.webp';
import scheme from '../assets/scheme.webp';


const ServiceHighlights = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 100, damping: 10 }
    },
    hover: {
      y: -5,
      boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      transition: { duration: 0.3 }
    }
  };

  const statVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  // Custom Stat Component with animation and hover effects
  const StatItem = ({ number, text, delay = 0 }) => {
    const [ref, inView] = useInView({
      triggerOnce: true,
      threshold: 0.1,
      rootMargin: '-50px 0px'
    });

    return (
      <motion.div
        ref={ref}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={statVariants}
        transition={{ duration: 0.5, delay }}
        whileHover={{
          scale: 1.05,
          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          background: "linear-gradient(135deg, #f6e05e 0%, #48bb78 100%)",
          color: "white"
        }}
        className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-center border-2 border-transparent"
      >
        <motion.h3
          className="text-4xl font-extrabold text-green-600 mb-3 hover:text-white"
          whileHover={{ scale: 1.1 }}
        >
          {number}
        </motion.h3>
        <p className="text-lg text-gray-700 font-medium hover:text-white">{text}</p>
      </motion.div>
    );
  };

  // Service cards data - Updated with imported images
  const services = [
    {
      title: "सरकारी योजनाएं",
      description: "किसानों के लिए सरकारी योजनाओं की पूरी जानकारी और आवेदन प्रक्रिया",
      image: scheme,
      link: "government-schemes",
      icon: "🏛️",
      details: [
        "प्रधानमंत्री किसान सम्मान निधि",
        "किसान क्रेडिट कार्ड योजना",
        "फसल बीमा योजना",
        "सब्सिडी पर कृषि यंत्र"
      ]
    },
    {
      title: "बीज जानकारी",
      description: "विभिन्न फसलों के उन्नत बीजों की जानकारी और उनका सही उपयोग",
      image: seed,
      link: "seed-information",
      icon: "🌾",
      details: [
        "उन्नत किस्मों के बीज",
        "बीज उपचार विधि",
        "बीज संरक्षण तकनीक",
        "बीज दर और बुआई विधि"
      ]
    },
    {
      title: "फसल रोग नियंत्रण",
      description: "फसलों में लगने वाले प्रमुख रोगों और कीटों की पहचान व नियंत्रण",
      image: croppest,
      link: "crop-pest-disease-control",
      icon: "🐛",
      details: [
        "रोगों की पहचान कैसे करें",
        "जैविक नियंत्रण विधियाँ",
        "रासायनिक उपचार",
        "समेकित नाशीजीव प्रबंधन"
      ]
    },
    {
      title: "कृषि यंत्र",
      description: "आधुनिक कृषि यंत्रों की जानकारी और उनके उपयोग के तरीके",
      image: machine,
      link: "farm-machinery",
      icon: "🚜",
      details: [
        "ट्रैक्टर और संलग्न यंत्र",
        "बुआई और कटाई मशीनें",
        "सिंचाई यंत्र",
        "यंत्रों पर सब्सिडी"
      ]
    },
    {
      title: "खेती मार्गदर्शन",
      description: "विशेषज्ञों द्वारा व्यक्तिगत खेती सलाह और मार्गदर्शन",
      image: about2,
      link: "farming-guidance",
      icon: "👨‍🌾",
      details: [
        "फसल चक्र नियोजन",
        "मौसम अनुसार सलाह",
        "मृदा स्वास्थ्य प्रबंधन",
        "उत्पादन लागत कम करें"
      ]
    },
    {
      title: "जल प्रबंधन",
      description: "पानी की बचत करते हुए कुशल सिंचाई तकनीकों की जानकारी",
      image: irrigation,
      link: "water-management",
      icon: "💧",
      details: [
        "ड्रिप सिंचाई प्रणाली",
        "स्प्रिंकलर सिस्टम",
        "जल संचयन तकनीक",
        "सिंचाई समय प्रबंधन"
      ]
    }
  ];

  // Testimonials data - Updated with imported images
  const testimonials = [
    {
      name: "राजेश कुमार",
      village: "कानपुर, उत्तर प्रदेश",
      quote: "मिट्टी परीक्षण की सलाह से मेरी यूरिया की खपत 40% कम हो गई और उत्पादन 20% बढ़ गया।",

    },
    {
      name: "सुनीता देवी",
      village: "मुजफ्फरपुर, बिहार",
      quote: "लीची की फसल में लगने वाले कीटों के जैविक नियंत्रण से मेरी फसल सुरक्षित रही।",

    },
    {
      name: "विजय पाटिल",
      village: "नासिक, महाराष्ट्र",
      quote: "ड्रिप सिंचाई अपनाने से मेरे पानी और खाद का खर्च कम हुआ और अंगूर की गुणवत्ता बेहतर हुई।",

    },
    {
      name: "गीता यादव",
      village: "इंदौर, मध्य प्रदेश",
      quote: "सोयाबीन की नई किस्म की जानकारी से मेरा उत्पादन पहले से डेढ़ गुना हो गया है।",

    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO Component with detailed metadata */}
      <SEO
        title="किसान सहायता सेवाएं | खेती की संपूर्ण जानकारी"
        description="किसान भाइयों के लिए खेती-किसानी से जुड़ी सभी जानकारी - फसल प्रबंधन, मिट्टी स्वास्थ्य, सिंचाई, कीट नियंत्रण और बाजार भाव"
        keywords="खेती सलाह, मिट्टी जांच, सिंचाई तकनीक, फसल रोग, कृषि यंत्र, बाजार भाव, किसान सहायता"
      />
      <div className="h-20"></div>
      {/* Hero Section with Gradient Background */}
      <div className="relative h-[400px] md:h-[500px] overflow-hidden">
        {/* Background Image - using object-contain to show full image */}
        <img
          src={HeroImage}
          alt="Hero Background"
          className="absolute w-full h-full object-cover object-center"
        />

        {/* Dark overlay for better text visibility */}
        <div className="absolute inset-0 bg-black/30"></div>

        {/* Content */}
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">किसान सहायता पोर्टल</h1>
            <p className="text-xl text-white max-w-3xl mx-auto">
              खेती-किसानी से जुड़ी सभी समस्याओं का समाधान - वैज्ञानिक तकनीक से बढ़ाएं अपनी आमदनी
            </p>
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Service Cards Section */}
        <section className="mb-24 mt-12">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">हमारी विशेष सेवाएं</h2>
            <div className="w-24 h-1 bg-green-500 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              किसान भाइयों की जरूरतों को ध्यान में रखकर तैयार की गई विशेष सुविधाएं
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={cardVariants}
                whileHover="hover"
                className="bg-white rounded-2xl overflow-hidden shadow-xl transition-all duration-300 flex flex-col group"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute top-4 right-4 bg-white/90 text-3xl p-3 rounded-full group-hover:bg-yellow-400 group-hover:text-white transition-colors duration-300">
                    {service.icon}
                  </div>
                </div>
                <div className="p-6 flex-grow group-hover:bg-gradient-to-br group-hover:from-green-50 group-hover:to-yellow-50 transition-colors duration-300">
                  <h3 className="text-2xl font-bold text-gray-800 mb-3 group-hover:text-green-700 transition-colors duration-300">{service.title}</h3>
                  <p className="text-gray-600 mb-4 group-hover:text-gray-800 transition-colors duration-300">{service.description}</p>
                  <ul className="mb-6 space-y-2">
                    {service.details.slice(0, 3).map((detail, i) => (
                      <li key={i} className="flex items-start group-hover:translate-x-1 transition-transform duration-300">
                        <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0 group-hover:text-yellow-500 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700 group-hover:text-gray-900 transition-colors duration-300">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="px-6 pb-6 group-hover:bg-gradient-to-br group-hover:from-green-50 group-hover:to-yellow-50 transition-colors duration-300 rounded-b-2xl">
                  <a
                    href={service.link}
                    className="inline-flex items-center px-5 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-300 font-medium group-hover:bg-gradient-to-r group-hover:from-green-500 group-hover:to-yellow-500 group-hover:shadow-lg"
                  >
                    और जानें
                    <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </a>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* Stats Section with Hover Effects */}
        <section className="mb-24 mt-12">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">हमारी उपलब्धियाँ</h2>
            <div className="w-24 h-1 bg-green-500 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              पूरे देश के किसान भाइयों के साथ मिलकर हमने ये मुकाम हासिल किया है
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <StatItem number="50,000+" text="संतुष्ट किसान" delay={0.1} />
            <StatItem number="200+" text="फसल तकनीकें" delay={0.2} />
            <StatItem number="75+" text="सरकारी योजनाएं" delay={0.3} />
            <StatItem number="1 लाख+" text="सफल सलाह" delay={0.4} />
          </div>
        </section>

        {/* Kisan Testimonials Section */}
        <section className="mb-24 mt-12">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">किसानों की सफलता की कहानियाँ</h2>
            <div className="w-24 h-1 bg-green-500 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              देखिए देशभर के किसान भाइयों ने हमारी सलाह से कैसे बदली अपनी खेती
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{
                  y: -10,
                  background: "linear-gradient(135deg, #f0fff4 0%, #feffe4 100%)",
                  borderColor: "#68d391"
                }}
                className="bg-white p-6 rounded-xl shadow-md transition-all duration-300 flex flex-col h-full border-2 border-transparent"
              >
                <div className="mb-4">
                  <h4 className="font-bold text-gray-800 group-hover:text-green-700 transition-colors duration-300">{testimonial.name}</h4>
                  <p className="text-gray-600 text-sm group-hover:text-gray-700 transition-colors duration-300">{testimonial.village}</p>
                </div>
                <div className="flex-grow">
                  <div className="text-green-500 text-3xl mb-2 group-hover:text-yellow-500 transition-colors duration-300">"</div>
                  <p className="text-gray-700 mb-4 group-hover:text-gray-900 transition-colors duration-300">{testimonial.quote}</p>
                </div>
                <div className="flex justify-center mt-auto">
                  <div className="flex space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400 group-hover:text-yellow-500 transition-colors duration-300" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-24 mt-12">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">किसानों के सामान्य सवाल</h2>
            <div className="w-24 h-1 bg-green-500 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              किसान भाइयों द्वारा अक्सर पूछे जाने वाले महत्वपूर्ण प्रश्न और उनके उत्तर
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {[
              {
                question: "खरीफ और रबी फसलों में क्या अंतर है?",
                answer: "खरीफ फसलें जून-जुलाई में बोई जाती हैं और अक्टूबर-नवंबर में काटी जाती हैं (जैसे धान, मक्का, सोयाबीन)। रबी फसलें अक्टूबर-नवंबर में बोई जाती हैं और मार्च-अप्रैल में काटी जाती हैं (जैसे गेहूं, चना, सरसों)।"
              },
              {
                question: "जैविक खेती कैसे शुरू करें?",
                answer: "सबसे पहले मिट्टी की जांच करवाएं। जैविक खाद (गोबर की खाद, वर्मीकम्पोस्ट) का उपयोग शुरू करें। कीट नियंत्रण के लिए नीम का तेल, गौमूत्र आदि का प्रयोग करें। धीरे-धीरे रासायनिक उर्वरकों का उपयोग कम करें।"
              },
              {
                question: "फसल चक्र क्यों जरूरी है?",
                answer: "फसल चक्र अपनाने से मिट्टी की उर्वरता बनी रहती है, कीट-रोगों का प्रकोप कम होता है और उत्पादन बढ़ता है। जैसे धान-गेहूं-मूंग की फसल चक्र अपना सकते हैं।"
              },
              {
                question: "ड्रिप सिंचाई के क्या फायदे हैं?",
                answer: "ड्रिप सिंचाई से 30-50% पानी की बचत होती है, खरपतवार कम उगते हैं, उर्वरक का सीधे पौधों को मिलता है और उत्पादन 20-50% तक बढ़ जाता है।"
              },
              {
                question: "मिट्टी की जांच कैसे करवाएं?",
                answer: "नजदीकी कृषि विज्ञान केंद्र या मान्यता प्राप्त प्रयोगशाला से मिट्टी परीक्षण किट लें। 6-8 इंच गहराई से मिट्टी के नमूने लेकर जांच के लिए भेजें। परिणाम आने पर कृषि विशेषज्ञ से सलाह लें।"
              },
              {
                question: "किसान क्रेडिट कार्ड कैसे प्राप्त करें?",
                answer: "सबसे पहले अपने नजदीकी बैंक शाखा में संपर्क करें। जमीन के कागजात, आधार कार्ड, पासपोर्ट साइज फोटो और बैंक खाता विवरण साथ ले जाएं। आवेदन पूरा होने के 15-20 दिनों में कार्ड मिल जाता है।"
              },
              {
                question: "फसल बीमा योजना का लाभ कैसे उठाएं?",
                answer: "फसल बुवाई के समय ही नजदीकी कृषि विभाग या अधिकृत बीमा कंपनी से संपर्क करें। आवश्यक दस्तावेज जमा करें और प्रीमियम भरें। फसल क्षति होने पर तुरंत रिपोर्ट दर्ज करवाएं।"
              },
              {
                question: "मौसम पूर्वानुमान कैसे जानें?",
                answer: "भारत मौसम विज्ञान विभाग (IMD) की वेबसाइट या मौसम ऐप देखें। कृषि विभाग द्वारा जारी किसान मौसम सेवा (Kisan Weather Service) का उपयोग करें। स्थानीय कृषि विस्तार अधिकारी से संपर्क करें।"
              },
              {
                question: "फसल अवशेष प्रबंधन कैसे करें?",
                answer: "फसल अवशेषों को जलाने के बजाय कंपोस्ट खाद में बदलें या मल्चिंग के लिए उपयोग करें। हैप्पी सीडर मशीन से फसल अवशेषों को खेत में ही मिला दें। इससे मिट्टी की उर्वरता बढ़ेगी।"
              },
              {
                question: "सब्जी की खेती में सबसे अधिक मुनाफा कैसे कमाएं?",
                answer: "मौसम के अनुसार सब्जियां चुनें, उन्नत किस्मों का प्रयोग करें, जैविक तरीके अपनाएं और सीधे मंडी या ऑनलाइन प्लेटफॉर्म पर बेचें। पॉलीहाउस में उगाकर ऑफ-सीजन में अधिक मूल्य प्राप्त करें।"
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{
                  scale: 1.02,
                  borderColor: "#68d391",
                  background: "linear-gradient(135deg, #f0fff4 0%, #ffffff 100%)"
                }}
                className="mb-6 bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 border-2 border-transparent"
              >
                <details className="group">
                  <summary className="list-none p-6 cursor-pointer flex justify-between items-center hover:bg-green-50 transition-colors duration-300">
                    <h3 className="text-xl font-bold text-gray-800 group-hover:text-green-700 transition-colors duration-300">{faq.question}</h3>
                    <svg className="w-6 h-6 text-green-600 transition-transform duration-300 group-open:rotate-180 group-hover:text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <div className="px-6 pb-6 -mt-3 bg-gradient-to-b from-green-50 to-white">
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                </details>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA Section - Updated with Newsletter */}
        <section className="bg-gradient-to-r from-green-500 to-green-700 rounded-2xl p-8 md:p-12 mt-12">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">हमसे जुड़ें</h2>
              <p className="text-xl text-green-100 mb-8">
                आधुनिक कृषि तकनीकों की जानकारी पाएं और बढ़ाएं अपनी आमदनी
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
                <motion.a
                  href="/contact" // Replace with your actual contact page link
                  whileHover={{
                    scale: 1.05,
                    background: "linear-gradient(135deg, #ffffff 0%, #f0ffe4 100%)",
                    color: "#2f855a"
                  }}
                  className="px-8 py-4 bg-white text-green-700 rounded-lg font-bold transition-all duration-300"
                >
                  संपर्क करें
                </motion.a>
                
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ServiceHighlights;